import type { AppIconInput } from "./appIcon";
import { resolveAppleSystemApp } from "./appleSystemApps";
import { friendlyAppName, isIdentifierShaped } from "./friendlyAppName";
import {
  isPackageSegmentStopword,
  isSignalAppToken,
  rankAppTokens,
  tokenizeAppSegments,
} from "./packageSegmentStopwords";

export type BrandfetchSearchCandidate = {
  brandId?: string | null;
  domain?: string | null;
  name?: string | null;
  icon?: string | null;
  qualityScore?: number | null;
  verified?: boolean | null;
};

export type BrandfetchSearchSelection = {
  brandfetchBrandId?: string;
  brandfetchDomain?: string;
  displayName: string;
  qualityScore?: number;
  score: number;
};

const MIN_CONFIDENCE_SCORE = 78;
const GENERIC_LOW_CONFIDENCE_NAMES = new Set(["mail", "freedom", "settings"]);

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function compactText(value: string): string {
  return normalizeText(value).replace(/\s+/g, "");
}

function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0]!
    .split("?")[0]!;
}

function normalizeIdentifier(value: string): string {
  return value.trim().toLowerCase().replace(/^web:/i, "");
}

function identifierSearchSource({ displayName, appIdentifier }: AppIconInput): string {
  const normalizedId = normalizeIdentifier(appIdentifier ?? "");
  if (normalizedId) return normalizedId;

  const trimmedDisplay = (displayName ?? "").trim();
  if (trimmedDisplay && isIdentifierShaped(trimmedDisplay, appIdentifier ?? undefined)) {
    return normalizeIdentifier(trimmedDisplay);
  }

  return "";
}

export function appBrandfetchLookupKey({ displayName, appIdentifier }: AppIconInput): string | null {
  const normalizedId = normalizeIdentifier(appIdentifier ?? "");
  if (normalizedId) return `id:${normalizedId}`;

  const friendly = friendlyAppName({ displayName, appIdentifier });
  const normalizedName = normalizeText(friendly);
  if (!normalizedName || normalizedName === "unknown app" || normalizedName === "unknown website") {
    return null;
  }
  return `name:${normalizedName}`;
}

const FRIENDLY_NAME_SEARCH_IDENTIFIERS = new Set([
  // Prime Video bundle tokenizes to "amazon" — search by resolved label instead.
  "com.amazon.aiv.aivapp",
]);

export function brandfetchSearchQueries(input: AppIconInput): string[] {
  const appleSystemApp = resolveAppleSystemApp(input);
  if (appleSystemApp) return [appleSystemApp.brandfetchSearchQuery];

  const normalizedId = normalizeIdentifier(input.appIdentifier ?? "");
  if (normalizedId && FRIENDLY_NAME_SEARCH_IDENTIFIERS.has(normalizedId)) {
    const friendly = friendlyAppName(input);
    const normalized = normalizeText(friendly);
    if (normalized && normalized !== "unknown app" && normalized !== "unknown website") {
      return [friendly];
    }
  }

  const identifierQueries = rankAppTokens(
    tokenizeAppSegments(identifierSearchSource(input)).filter(isSignalAppToken),
  );
  if (identifierQueries.length > 0) return identifierQueries;

  const friendly = friendlyAppName(input);
  const normalized = normalizeText(friendly);
  if (!normalized || normalized === "unknown app" || normalized === "unknown website") return [];
  if (isPackageSegmentStopword(normalized)) return [];
  return [friendly];
}

export function brandfetchSearchQuery(input: AppIconInput): string | null {
  return brandfetchSearchQueries(input)[0] ?? null;
}

export function brandfetchSearchUrl(query: string, clientId: string): string {
  return `https://api.brandfetch.io/v2/search/${encodeURIComponent(query)}?c=${encodeURIComponent(clientId)}`;
}

function candidateScore(query: string, candidate: BrandfetchSearchCandidate): number {
  const normalizedQuery = normalizeText(query);
  const compactQuery = compactText(query);
  const candidateName = normalizeText(candidate.name ?? "");
  const compactName = compactText(candidate.name ?? "");
  const domain = normalizeDomain(candidate.domain ?? "");
  const domainStem = normalizeText(domain.split(".")[0] ?? "");
  const compactDomainStem = compactText(domainStem);
  const quality = typeof candidate.qualityScore === "number" ? candidate.qualityScore : 0;

  let score = Math.min(Math.max(quality, 0), 1) * 25;

  if (candidate.verified) score += 18;
  if (candidate.brandId) score += 4;
  if (candidate.domain) score += 4;
  if (candidate.icon) score += 2;

  if (candidateName === normalizedQuery) score += 45;
  else if (compactName === compactQuery) score += 38;
  else if (candidateName.includes(normalizedQuery) || normalizedQuery.includes(candidateName)) score += 18;

  if (domainStem === normalizedQuery || compactDomainStem === compactQuery) score += 24;
  else if (compactDomainStem.includes(compactQuery) || compactQuery.includes(compactDomainStem)) score += 10;

  if (GENERIC_LOW_CONFIDENCE_NAMES.has(normalizedQuery) && candidateName !== normalizedQuery) {
    score -= 20;
  }

  return score;
}

export function selectBrandfetchSearchResult(
  query: string,
  candidates: BrandfetchSearchCandidate[],
): BrandfetchSearchSelection | null {
  const scored = candidates
    .filter((candidate) => candidate.brandId || candidate.domain)
    .map((candidate) => ({ candidate, score: candidateScore(query, candidate) }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  if (!best || best.score < MIN_CONFIDENCE_SCORE) return null;

  const brandfetchDomain = best.candidate.domain ? normalizeDomain(best.candidate.domain) : undefined;
  const brandfetchBrandId = best.candidate.brandId?.trim() || undefined;
  if (!brandfetchDomain && !brandfetchBrandId) return null;

  return {
    brandfetchBrandId,
    brandfetchDomain,
    displayName: best.candidate.name?.trim() || query,
    qualityScore: typeof best.candidate.qualityScore === "number" ? best.candidate.qualityScore : undefined,
    score: best.score,
  };
}
